import API from "../../services/api";
import { calcShiftDurationHours, isOvernightShift } from "../../components/roster/rosterUtils";

export const HOSPITAL_SHIFT_TYPES = [
  { value: "general", label: "General shift" },
  { value: "morning", label: "Morning shift" },
  { value: "evening", label: "Evening shift" },
  { value: "night", label: "Night shift" },
  { value: "rotational", label: "Rotational shift" },
  { value: "split", label: "Split shift" },
  { value: "flexible", label: "Flexible shift" },
  { value: "on_call", label: "On-call shift" },
  { value: "emergency", label: "Emergency shift" },
  { value: "icu", label: "ICU shift" },
  { value: "weekend", label: "Weekend shift" },
  { value: "holiday", label: "Holiday shift" },
];

export const DEFAULT_SHIFT_CONFIG = {
  break_start: "",
  break_end: "",
  min_working_hours: 8,
  max_working_hours: 12,
  next_day_ending: false,
  auto_clock_out: false,
  overtime_eligible: true,
  flexible_timing: false,
  rotational_eligible: false,
  biometric_required: true,
  geo_fencing_enabled: false,
  grace_minutes: 15,
  late_threshold_minutes: 30,
  early_exit_threshold_minutes: 15,
  half_day_threshold_minutes: 240,
  absent_threshold_minutes: 480,
  overtime_threshold_hours: 8,
  weekly_offs: [0],
  alternate_weekends: false,
};

export async function fetchShiftTemplates(params = {}) {
  const res = await API.get("/wf/shift-templates", { params });
  const data = res.data;
  if (data.items) return data;
  return { items: data, total: data.length, page: 1, page_size: data.length };
}

export async function fetchShiftTemplate(templateId) {
  const res = await API.get(`/wf/shift-templates/${templateId}`);
  return res.data;
}

export async function createShiftTemplate(payload) {
  const res = await API.post("/wf/shift-templates", payload);
  return res.data;
}

export async function updateShiftTemplate(templateId, payload) {
  const res = await API.patch(`/wf/shift-templates/${templateId}`, payload);
  return res.data;
}

export async function deleteShiftTemplate(templateId) {
  await API.delete(`/wf/shift-templates/${templateId}`);
}

export async function archiveShiftTemplate(templateId) {
  const res = await API.post(`/wf/shift-templates/${templateId}/archive`);
  return res.data;
}

export async function duplicateShiftTemplate(templateId, newCode) {
  const res = await API.post(`/wf/shift-templates/${templateId}/duplicate`, { new_code: newCode });
  return res.data;
}

export async function fetchShiftAudit(templateId) {
  const res = await API.get(`/wf/shift-templates/${templateId}/audit`);
  return res.data;
}

export function templateToForm(t) {
  const cfg = { ...DEFAULT_SHIFT_CONFIG, ...(t.config_json || {}) };
  return {
    code: t.code || "",
    name: t.name || "",
    shift_type: t.shift_type || "general",
    description: t.description || "",
    department_id: t.department_id || "",
    color_tag: t.color_tag || "#0d9488",
    start_time: t.start_time || "09:00",
    end_time: t.end_time || "18:00",
    break_minutes: t.break_minutes ?? 60,
    cross_midnight: t.cross_midnight || false,
    night_shift: t.night_shift || false,
    capacity: t.capacity ?? "",
    is_active: t.is_active !== false,
    policy_id: t.policy_id || "",
    config_json: cfg,
  };
}

export function applyShiftTiming(form, updates) {
  const start_time = updates.start_time ?? form.start_time;
  const end_time = updates.end_time ?? form.end_time;
  const break_minutes = updates.break_minutes ?? form.break_minutes;
  const cross_midnight = isOvernightShift(start_time, end_time);
  const totalHours = calcShiftDurationHours(start_time, end_time, break_minutes);
  return {
    ...form,
    ...updates,
    start_time,
    end_time,
    break_minutes,
    cross_midnight,
    config_json: {
      ...form.config_json,
      min_working_hours: totalHours,
      next_day_ending: cross_midnight,
    },
  };
}

export function formToPayload(form) {
  const cross_midnight = isOvernightShift(form.start_time, form.end_time);
  const totalHours = calcShiftDurationHours(form.start_time, form.end_time, form.break_minutes);
  const config_json = {
    ...form.config_json,
    min_working_hours: totalHours,
    next_day_ending: cross_midnight,
  };
  return {
    code: form.code.trim().toUpperCase(),
    name: form.name.trim(),
    shift_type: form.shift_type,
    description: form.description || null,
    department_id: form.department_id || null,
    color_tag: form.color_tag,
    start_time: form.start_time,
    end_time: form.end_time,
    break_minutes: Number(form.break_minutes) || 0,
    cross_midnight,
    night_shift: form.night_shift || cross_midnight,
    capacity: form.capacity ? Number(form.capacity) : null,
    is_active: form.is_active,
    policy_id: form.policy_id || null,
    config_json,
  };
}
